import { useEffect, useRef, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Ban, RotateCcw } from "lucide-react";
import { PaginationBar } from "@/components/shared/PaginationBar";
import VendorFormDialog, { prefetchVendor, warmVendorLookups } from "./VendorFormDialog";
import { invalidateRef } from "../paymentDesk/shared/refDataStore";

const MANAGE_ROLES = ["Director", "Manager", "Back Office"];

// Vendor Master — Vendors tab. System-generated permanent codes
// (VEN-<CAT>-<YEAR>-####), single bank-details record, GSTIN
// format-validated, soft-deactivate only. Director/Manager/Back Office
// have full CRUD; Accounts views read-only (no create/edit/deactivate).
export default function VendorsTab() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [formTarget, setFormTarget] = useState(null);
  const [busyCode, setBusyCode] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const reqId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { warmVendorLookups(); }, []);

  useEffect(() => {
    client.get("/vendor-master/categories", { params: { include_inactive: true } }).then((res) => setCategories(res.data)).catch(() => setCategories([]));
  }, []);

  const load = (pageNum = 1) => {
    setLoading(true);
    const myReq = ++reqId.current;
    const params = { page: pageNum, page_size: 50 };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (status !== "all") params.status = status;
    if (categoryFilter !== "all") params.category_id = categoryFilter;
    client
      .get("/vendor-master/vendors", { params })
      .then((res) => {
        if (myReq !== reqId.current) return;
        setVendors(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => { if (myReq === reqId.current) toast.error(getErrorMessage(err)); })
      .finally(() => { if (myReq === reqId.current) setLoading(false); });
  };

  useEffect(() => { load(1); }, [debouncedSearch, status, categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleActive = async (v) => {
    setBusyCode(v.vendor_code);
    try {
      await client.post(`/vendor-master/vendors/${v.vendor_code}/${v.active ? "deactivate" : "reactivate"}`);
      toast.success(v.active ? "Vendor deactivated" : "Vendor reactivated");
      invalidateRef("vendors");
      load(page);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="vendors-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="vendor-search-input" placeholder="Search by code, name or GSTIN" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <SearchableSelect testId="vendor-category-filter" className="w-48" value={categoryFilter} onValueChange={setCategoryFilter} placeholder="All Categories"
  options={[{ value: "all", label: "All Categories", pinned: true }, ...categories.map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }))]} />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="vendor-status-filter" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <Button data-testid="vendor-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Vendor
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="vendor-table">
          <TableHeader>
            <TableRow>
              <TableHead>Vendor Code</TableHead>
              <TableHead>Vendor Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>Bank Details</TableHead>
              <TableHead>Status</TableHead>
              {canManage && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : vendors.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No vendors found.</TableCell></TableRow>
            ) : (
              vendors.map((v) => (
                <TableRow key={v.vendor_code} data-testid={`vendor-row-${v.vendor_code}`} className="cursor-pointer" onMouseEnter={() => prefetchVendor(v.vendor_code).catch(() => {})} onClick={() => setFormTarget(v)}>
                  <TableCell className="font-mono text-xs font-semibold">{v.vendor_code}</TableCell>
                  <TableCell>{v.vendor_name}</TableCell>
                  <TableCell className="text-xs">{v.category_name ? `${v.category_name} (${v.category_code})` : "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{v.gstin || "—"}</TableCell>
                  <TableCell>
                    <Badge className={v.bank_details_complete ? "bg-emerald-600" : "bg-slate-400"}>
                      {v.bank_details_complete ? "Complete" : "Incomplete"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={v.active === false ? "bg-slate-400" : "bg-emerald-600"}>{v.active === false ? "Inactive" : "Active"}</Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant={v.active === false ? "outline" : "destructive"}
                        disabled={busyCode === v.vendor_code}
                        data-testid={`vendor-toggle-active-${v.vendor_code}`}
                        onClick={() => toggleActive(v)}
                      >
                        {v.active === false ? <RotateCcw className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="vendor-pagination" />

      {formTarget && (
        <VendorFormDialog
          vendor={formTarget === "new" ? null : formTarget}
          readOnly={formTarget !== "new" && !canManage}
          onClose={() => setFormTarget(null)}
          onSaved={() => {
            setFormTarget(null);
            load(page);
          }}
        />
      )}
    </div>
  );
}
